"use client";

import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { apiShowcaseConfig, Language, languages, modes } from "./code-data";
import { SyntaxHighlighter } from "./syntax-highlighter";
import { classNames } from "@/helpers/tailwind-class";
import { webAssetsCdnBaseUrl } from "@/shared/constants";

export function HeroSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>("python");

  function next() {
    if (document.startViewTransition) {
      document.startViewTransition(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % modes.length);
      });
    } else {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % modes.length);
    }
  }

  const codeConfig = apiShowcaseConfig[modes[currentIndex]!];

  return (
    <div
      className="relative w-full bg-black"
      style={{
        minHeight: "max(100vh, 1080px)",
      }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={codeConfig.mediaKey}
          initial={{ opacity: 0.5 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          className="absolute h-full w-full"
        >
          <video
            playsInline
            muted
            autoPlay
            className="h-full w-full object-cover opacity-50"
            onEnded={() => {
              next();
            }}
            poster={`${webAssetsCdnBaseUrl}/api-assets/examples/${codeConfig.mediaKey}.webp`}
          >
            <source
              src={`${webAssetsCdnBaseUrl}/api-assets/examples/${codeConfig.mediaKey}.webm`}
              type="video/webm"
            />
            <source
              src={`${webAssetsCdnBaseUrl}/api-assets/examples/${codeConfig.mediaKey}-compressed.mp4`}
              type="video/mp4"
            />
          </video>
        </motion.div>
      </AnimatePresence>
      <div className="text-neutral-content flex w-full translate-y-24 flex-col items-center text-center sm:translate-y-32">
        <div className="flex w-full flex-col gap-8 px-2 text-white">
          <h1 className="mx-auto max-w-4xl text-center text-4xl leading-tight font-bold tracking-wide sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl">
            AI Video & Image Creation API
          </h1>

          <p className="mx-auto max-w-3xl text-center text-base tracking-wide text-slate-200 sm:text-lg">
            Integrate generative AI video and image capabilities to your app in minutes. Built for
            developers, by developers.
          </p>

          <Link
            className={classNames("btn btn-primary btn-lg mx-auto w-fit rounded-full text-white")}
            href="https://docs.magichour.ai"
            target="_blank"
          >
            Get Started For Free
          </Link>

          <div className="mx-auto min-h-[100px] w-full max-w-xl rounded-lg bg-slate-950/90 text-left">
            <div
              role="tablist"
              className="tabs box-border flex border border-b-2 border-b-slate-800"
            >
              {languages.map((lang) => {
                return (
                  <button
                    role="tab"
                    className={classNames(
                      "tab -mb-0.5 box-border w-fit capitalize",
                      selectedLanguage === lang ? "tab-active" : "border-b-0!"
                    )}
                    key={lang}
                    onClick={() => {
                      setSelectedLanguage(lang);
                    }}
                  >
                    {lang === "http" ? "HTTP" : lang}
                  </button>
                );
              })}
            </div>

            <SyntaxHighlighter
              language={selectedLanguage}
              code={codeConfig.codeSnippet[selectedLanguage].trim()}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
